import { Bot, User } from 'lucide-react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { RateLimitCountdown } from './RateLimitCountdown';
import { TypingIndicator } from './TypingIndicator';
import { useTheme } from '../../../contexts/ThemeContext';

export const ChatMessage = ({ message }) => {
  if (message.role === 'assistant' && message.isTyping && !message.content) {
    return <TypingIndicator />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="w-full"
    >
      <div className={`flex items-start gap-2.5 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
        {message.role === 'assistant' && <MessageAvatar icon={Bot} />}
        <div className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'} max-w-[80%]`}>
          <MessageContent message={message} />
          {message.isError && message.retryAfter > 0 && (
            <RateLimitCountdown seconds={message.retryAfter} />
          )}
        </div>
        {message.role === 'user' && <MessageAvatar icon={User} />}
      </div>
    </motion.div>
  );
};

const MessageAvatar = ({ icon: Icon }) => {
  const { isDark } = useTheme();
  return (
    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
      isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-neutral-100 text-neutral-500'
    }`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
  );
};

const MessageContent = ({ message }) => {
  const { isDark } = useTheme();
  return (
    <div
      className={`rounded-lg px-3 py-2 text-sm ${
        message.role === 'user'
          ? 'bg-accent-500 text-white'
          : message.isError
          ? isDark
            ? 'bg-red-500/10 text-red-300 border border-red-500/20'
            : 'bg-red-50 text-red-700 border border-red-200'
          : isDark
            ? 'bg-neutral-800 text-neutral-200'
            : 'bg-neutral-100 text-neutral-800'
      } w-full`}
    >
      {message.role === 'user' ? (
        <UserMessage content={message.content} />
      ) : (
        <AssistantMessage message={message} />
      )}
    </div>
  );
};

const UserMessage = ({ content }) => (
  <p className="leading-relaxed text-left whitespace-pre-wrap break-words">
    {content}
  </p>
);

const AssistantMessage = ({ message }) => (
  <div className="markdown-content w-full">
    <ReactMarkdown
      components={{
        p: 'p', h1: 'h1', h2: 'h2', h3: 'h3',
        ul: 'ul', ol: 'ol', li: 'li',
        code: 'code', pre: 'pre', blockquote: 'blockquote',
      }}
    >
      {message.content}
    </ReactMarkdown>
    {message.isTyping && <TypingIndicator inline />}
  </div>
); 